// Libraries
import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";

// Components
import ImageWithBasePath from "../../../../../core/common/imageWithBasePath";
import ShowMoreModal from "../../../common/components/ShowMoreModal";
import ReportViewModal from "../../../common/components/ReportViewModal";
import ViewAttachmentsModal from "../../../common/components/ViewAttachmentsModal";

// Helpers
import { getStatusConfig, formatDate } from "../helpers/upcomingReports.helper";
import { getFullName } from "../../../reports-settings/report-type/helpers/form.handler";
import { BackendAllReport } from "../../../common/types/report";
import { exportReportToPDF } from "../../../common/helpers";
import { humanReadableDate } from "../../../../../helper/dateTimeHelper";

const AllReportsCard: React.FC<{
  report: BackendAllReport;
  onDownloadReport?: (reportId: number) => void;
}> = ({ report, onDownloadReport }) => {
  const [selectedPreviewReport, setSelectedPreviewReport] = useState<any>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState(false);
  const [isReportViewModalOpen, setIsReportViewModalOpen] = useState(false);
  const [isViewAttachmentsModalOpen, setIsViewAttachmentsModalOpen] =
    useState(false);

  const handleCloseModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setIsShowMoreModalOpen(false);
    setIsReportViewModalOpen(false);
    setIsViewAttachmentsModalOpen(false);
  }, []);

  const handleShowMore = useCallback(() => {
    setIsShowMoreModalOpen(true);
  }, []);
  const handleViewReport = (report: any) => {
    setSelectedPreviewReport(report);
    setIsReportViewModalOpen(true);
  };
  const handleViewAttachments = (attachments: any) => {
    setSelectedAttachments(attachments);
    setIsViewAttachmentsModalOpen(true);
  };

  const reportsSortOptions = [
    {
      value: "report_date-ASC",
      label: "Report Date [Old - New]",
    },
    {
      value: "report_date-DESC",
      label: "Report Date [New - Old]",
    },
    {
      value: "submitted_at-DESC",
      label: "Submission Date [New - Old]",
    },
    {
      value: "submitted_at-ASC",
      label: "Submission Date [Old - New]",
    },
    {
      value: "report_type_id-ASC",
      label: "Report Type [Ascending]",
    },
    {
      value: "report_type_id-DESC",
      label: "Report Type [DESC]",
    },
  ];

  // Convert backend status to frontend format for helper functions
  const frontendStatus = report?.status_text?.toLowerCase() as
    | "completed"
    | "pending"
    | "draft";
  const statusConfig = getStatusConfig(frontendStatus);

  const showMaxItems = 1;
  const displayNames = report?.visible_to.slice(0, showMaxItems);
  const hasMore = report?.visible_to.length > showMaxItems;
  const modalDisplayNames = report?.visible_to.slice(showMaxItems);

  // Format dates from backend
  const createdDate = formatDate(report.submitted_at);

  // Enhanced content preview for TipTap HTML content
  const createRichContentPreview = (
    htmlContent: string
  ): { preview: string; hasMore: boolean } => {
    if (!htmlContent)
      return { preview: "No content available", hasMore: false };

    // Sanitize the HTML content
    const sanitizedContent = DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
      ],
      ALLOWED_ATTR: [],
    });

    // Create preview by limiting content length
    const maxChars = 200;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sanitizedContent;

    const fullText = tempDiv.textContent || tempDiv.innerText || "";
    const hasMore = fullText.length > maxChars;

    if (!hasMore) {
      return { preview: sanitizedContent, hasMore: false };
    }

    // Truncate HTML content while preserving structure
    let truncatedHtml = "";
    let charCount = 0;
    let shouldBreak = false;

    const walkNodes = (node: Node) => {
      if (shouldBreak) return;

      if (node.nodeType === Node.TEXT_NODE) {
        const textContent = node.textContent || "";
        if (charCount + textContent.length > maxChars) {
          const remainingChars = maxChars - charCount;
          truncatedHtml += textContent.substring(0, remainingChars) + "...";
          shouldBreak = true;
        } else {
          truncatedHtml += textContent;
          charCount += textContent.length;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        truncatedHtml += `<${element.tagName.toLowerCase()}>`;

        for (let child of Array.from(node.childNodes)) {
          walkNodes(child);
          if (shouldBreak) break;
        }

        if (!shouldBreak) {
          truncatedHtml += `</${element.tagName.toLowerCase()}>`;
        }
      }
    };

    // Parse and walk the DOM tree
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizedContent, "text/html");

    for (let child of Array.from(doc.body.childNodes)) {
      walkNodes(child);
      if (shouldBreak) break;
    }

    return {
      preview: DOMPurify.sanitize(truncatedHtml),
      hasMore: true,
    };
  };

  const { preview: contentPreview, hasMore: hasMoreContent } =
    createRichContentPreview(report.report_content);

  const getFrequencyDisplayText = (): string => {
    const { frequency_type, weekly_interval, monthly_interval } = report;
    if (!frequency_type) {
      return "Custom";
    }

    switch (frequency_type) {
      case "daily":
        return "Daily";
      case "weekly":
        return weekly_interval === 1
          ? "Weekly"
          : `Every ${weekly_interval} weeks`;
      case "monthly":
        return monthly_interval === 1
          ? "Monthly"
          : `Every ${monthly_interval} months`;
      default:
        return frequency_type.charAt(0).toUpperCase() + frequency_type.slice(1);
    }
  };

  return (
    <>
      <div className="card report-card border-0 mb-4 shadow-sm">
        <div className="card-body p-3 bg-white">
          <div className="row align-items-center">
            {/* Report Info Section */}
            <div className="col-lg-4">
              <div className="d-flex align-items-center mb-2">
                <div className="me-3">
                  <i
                    className={`ti ti-file-analytics ${statusConfig.iconColor} fs-24`}></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="report-name mb-2 text-dark">
                    {report.report_title}
                  </h6>

                  <div className="d-flex align-items-center gap-2 mb-2">
                    <small className="badge badge-sm badge-secondary-transparent d-flex align-items-center">
                      <i className="ti ti-repeat me-1"></i>
                      {getFrequencyDisplayText()}
                    </small>
                    <small
                      className={`badge badge-sm badge-${report.is_submitted_late ? "danger" : "success"
                        } d-flex align-items-center`}>
                      {report.is_submitted_late ? "Late" : "On Time"}
                    </small>
                    <small className="badge badge-sm badge-info d-flex align-items-center">
                      {report?.created_by?.department_name}
                    </small>

                    {/* Add late submission,etc  */}
                  </div>
                  <small className="text-muted d-block mb-1">
                    <i className="ti ti-calendar me-1"></i>
                    Report Date: {humanReadableDate(report.report_date)}
                  </small>
                  <small className="text-muted d-block">
                    <i className="ti ti-calendar me-1"></i>
                    Submission Date: {humanReadableDate(report.submitted_at, true)}
                  </small>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              {/* Rich Content Preview */}
              <div className="content-preview-section mb-2">
                <div
                  className="content-preview-body"
                  style={{
                    maxHeight: "80px",
                    overflow: "hidden",
                    position: "relative",
                  }}>
                  {/* Render sanitized HTML with custom styles */}
                  <div
                    dangerouslySetInnerHTML={{ __html: contentPreview }}
                    style={{
                      fontSize: "0.7rem",
                      lineHeight: "1",
                      color: "#6c757d",
                    }}
                    className="tiptap-preview"
                  />
                  {/* Fade overlay for long content */}
                  {hasMoreContent && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "20px",
                        background: "linear-gradient(transparent, white)",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="w-100 d-flex align-items-center justify-content-start">
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <button
                    className="btn btn-info btn-sm px-2 py-1"
                    title="View Full Report"
                    onClick={() => handleViewReport(report)}>
                    <small className="d-flex align-items-center justify-content-center">
                      <i className="ti ti-eye me-1"></i>
                      View Full Report
                    </small>
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-sm px-2 py-1"
                    onClick={() =>
                      handleViewAttachments(report.report_attachments)
                    }
                    title="View Attachments"
                    disabled={report.attachments_count === 0}>
                    <small className="d-flex align-items-center justify-content-center">
                      <i className="ti ti-paperclip me-1"></i>
                      {report.attachments_count} Attachment
                      {report.attachments_count !== 1 ? "s" : ""}
                    </small>
                  </button>

                  <button
                    className="btn btn-success btn-sm px-2 py-1"
                    title="View Full Report"
                    onClick={() => exportReportToPDF(report)}>
                    <small className="d-flex align-items-center justify-content-center">
                      <i className="ti ti-file-export me-1"></i>
                      Download
                    </small>
                  </button>
                </div>
              </div>
            </div>

            {/* Submitted By*/}
            <div className="col-lg-2">
              <div className="assignment-section">
                <div className="badge badge-secondary-transparent d-flex align-items-center mb-2">
                  <i className={`ti ti-user text-secondary me-2 fs-18`}></i>
                  <h6 className="text-dark mb-0">Submitted By</h6>
                </div>
                <div className="px-1 assignment-item mb-1 d-flex align-items-center">
                  <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
                    <ImageWithBasePath
                      src={
                        !report?.created_by?.profile_pic ||
                          report?.created_by?.profile_pic === "null"
                          ? "assets/img/users/user.png"
                          : report?.created_by?.profile_pic
                      }
                      alt="img"
                    />
                  </li>

                  <span className={`text-muted me-1`}>
                    {getFullName(
                      report?.created_by?.salutation,
                      report?.created_by?.fname,
                      report?.created_by?.lname
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-lg-2">
              <div className="assignment-section">
                <div className="badge badge-secondary-transparent d-flex align-items-center mb-2">
                  <i className={`ti ti-user text-secondary me-2 fs-18`}></i>
                  <h6 className="text-dark mb-0">Other Viewers</h6>
                </div>

                <div className="assignment-list">
                  {report.visible_to.length == 0 ? (
                    <div className="px-1 assignment-item mb-1 d-flex align-items-center">
                      <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0"></li>
                      <span className={`text-muted me-1`}>
                        No records to show!
                      </span>
                    </div>
                  ) : (
                    <>
                      {displayNames.map((item: any) => (
                        <div
                          key={item?.id}
                          className="px-1 assignment-item mb-1 d-flex align-items-center">
                          <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
                            <ImageWithBasePath
                              src={
                                !item?.profile_pic ||
                                  item?.profile_pic === "null"
                                  ? "assets/img/users/user.png"
                                  : item?.profile_pic
                              }
                              alt="img"
                            />
                          </li>

                          <span className={`text-muted me-1`}>
                            {getFullName(
                              item?.salutation,
                              item?.fname,
                              item?.lname
                            )}
                            {hasMore && (
                              <Link
                                to="#"
                                className="px-2 btn-link text-decoration-none"
                                onClick={handleShowMore}>
                                <small className="text-primary">
                                  +{report?.visible_to.length - showMaxItems}{" "}
                                  more
                                </small>
                              </Link>
                            )}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ShowMoreModal
        isOpen={isShowMoreModalOpen}
        onClose={handleCloseModal}
        items={modalDisplayNames}
      />

      <ReportViewModal
        isOpen={isReportViewModalOpen}
        onClose={handleCloseModal}
        report={selectedPreviewReport}
        showCreatedBy={true}
      />

      <ViewAttachmentsModal
        isOpen={isViewAttachmentsModalOpen}
        onClose={handleCloseModal}
        attachments={selectedAttachments}
      />
    </>
  );
};

export default AllReportsCard;
